import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useParams } from "react-router-dom";
import axios from "axios";
import { SERVICES, ARTICLES as FALLBACK_ARTICLES, WHY_US, COMPANY } from "./data/content";
import { ArrowUpRight, Phone, Mail, MapPin, Check, ChevronRight } from "lucide-react";
import { AdminLogin, AdminDashboard } from "./Admin";
import { WhatsAppFloat } from "./components/WhatsAppFloat";

// Articles loaded from API with fallback to static seed
let __ARTICLES_CACHE = null;
function useArticles() {
  const [items, setItems] = useState(__ARTICLES_CACHE || FALLBACK_ARTICLES);
  useEffect(() => {
    axios.get(`${API}/articles`).then((r) => {
      if (r.data && r.data.length) { __ARTICLES_CACHE = r.data; setItems(r.data); }
    }).catch(()=>{});
  }, []);
  return items;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============ Reveal hook ============ */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ============ Nav ============ */
function Nav() {
  return (
    <nav className="ax-nav" data-testid="main-nav">
      <Link to="/" className="ax-monogram" data-testid="logo-home">
        AXIOM<span>.</span>
      </Link>
      <div className="ax-nav-links-desktop flex gap-8 items-center">
        <a href="/#expertise" className="ax-nav-link" data-testid="nav-expertise">Expertise</a>
        <a href="/#about" className="ax-nav-link" data-testid="nav-about">À propos</a>
        <Link to="/blog" className="ax-nav-link" data-testid="nav-blog">Journal</Link>
        <a href="/#contact" className="ax-nav-link" data-testid="nav-contact">Contact</a>
      </div>
      <a href="tel:+33766108964" className="btn-gold !py-2 !px-4 !text-[0.7rem]" data-testid="nav-cta-call">
        <Phone size={13} /> 07 66 10 89 64
      </a>
    </nav>
  );
}

/* ============ Hero ============ */
function Hero() {
  return (
    <section className="paper-bg pt-36 pb-24 px-6 md:px-12 relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto">
        <div className="ax-eyebrow mb-8">— Cabinet de Conseil · Depuis le Nord de France</div>
        <h1 className="ax-h1">
          <span className="block">Bâtir des</span>
          <span className="block italic gold-gradient">entreprises</span>
          <span className="block">qui durent.</span>
        </h1>
        <div className="mt-12 grid md:grid-cols-2 gap-12 items-end">
          <p className="font-sans-ax text-lg md:text-xl leading-relaxed text-neutral-700 max-w-xl">
            AXIOM accompagne dirigeants, créateurs d'entreprise et particuliers à chaque étape de leur projet — conseil, financement, formation, ressources humaines. Sur mesure, partout en France.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="#contact" className="btn-gold" data-testid="hero-cta-contact">Démarrer un projet <ArrowUpRight size={16} /></a>
            <a href="#expertise" className="btn-outline-ink" data-testid="hero-cta-services">Nos expertises</a>
          </div>
        </div>
        <div className="hero-line mt-24" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12 font-sans-ax text-sm">
          {[
            ["B2B", "Professionnels"],
            ["B2C", "Particuliers"],
            ["FRA", "Intervention nationale"],
            ["NDA", "Confidentialité totale"],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="font-mono-ax text-xs tracking-widest text-neutral-500">{k}</div>
              <div className="mt-1 text-neutral-800">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Marquee ============ */
function Marquee() {
  const words = ["Stratégie", "Création", "Financement", "Business Plan", "Formation", "Coaching", "RH & Social", "BTP", "Juridique", "Patrimoine"];
  const loop = [...words, ...words];
  return (
    <div className="marquee" data-testid="marquee">
      <div className="marquee-track">
        {loop.map((w, i) => (
          <React.Fragment key={i}>
            <span className="marquee-item">{w}</span>
            <span className="marquee-dot">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ============ About ============ */
function About() {
  return (
    <section id="about" className="ink-bg py-28 px-6 md:px-12" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 reveal">
            <div className="ax-eyebrow !text-[var(--ax-gold)] mb-6">— Notre histoire</div>
            <h2 className="ax-h2 text-[var(--ax-cream)]">
              Une équipe<br/><span className="italic gold-gradient">d'entrepreneurs</span><br/>au service<br/>des projets.
            </h2>
          </div>
          <div className="md:col-span-7 font-sans-ax space-y-8 text-lg leading-relaxed text-neutral-300 reveal">
            <p>
              AXIOM est une structure de conseil et d'accompagnement créée par plusieurs entrepreneurs expérimentés, dirigeants de plusieurs sociétés dans le Nord de la France.
            </p>
            <p>
              Fort de nombreuses années d'expérience dans la création d'entreprises, nos experts ont décidé de mettre leurs compétences au service des autres. Sollicités régulièrement par un réseau important pour des conseils en gestion, création, financement et accompagnement, AXIOM a vu le jour pour structurer et valoriser cette expertise.
            </p>
            <div className="pt-6 border-t border-neutral-700">
              <div className="ax-eyebrow !text-[var(--ax-gold)] mb-3">— Notre philosophie</div>
              <p className="italic font-display text-2xl text-[var(--ax-cream)] leading-snug">
                « Chaque projet mérite un accompagnement sur mesure. Nous intervenons avec proximité, disponibilité et professionnalisme, auprès des professionnels comme des particuliers, partout en France. »
              </p>
            </div>
            <div className="pt-6">
              <div className="ax-eyebrow !text-[var(--ax-gold)] mb-5">— Pourquoi nous choisir</div>
              <ul className="grid sm:grid-cols-2 gap-3">
                {WHY_US.map((w, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-200" data-testid={`why-us-${i}`}>
                    <Check size={18} className="text-[var(--ax-gold)] mt-1 flex-shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ Services ============ */
function Services() {
  return (
    <section id="expertise" className="paper-bg py-28 px-6 md:px-12" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 reveal">
          <div>
            <div className="ax-eyebrow mb-4">— 08 Domaines d'expertise</div>
            <h2 className="ax-h2">Nos <span className="italic gold-gradient">domaines</span></h2>
          </div>
          <p className="font-sans-ax text-neutral-600 max-w-md">
            Une offre intégrée pour les professionnels et les particuliers, du diagnostic à la mise en œuvre.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[var(--ax-line)]">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              className="service-card reveal !border-t-0 !border-l-0 !border-r !border-b"
              style={{ borderColor: 'var(--ax-line)' }}
              data-testid={`service-card-${s.slug}`}
            >
              <div className="service-num">/{String(i + 1).padStart(2, "0")}</div>
              <h3 className="service-title">{s.title}</h3>
              <p className="service-short">{s.short}</p>
              <ul className="mt-5 space-y-1.5 font-sans-ax text-sm opacity-90">
                {s.items.slice(0, 4).map((it, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <span className="text-[var(--ax-gold)] mt-1">—</span>
                    <span>{it}</span>
                  </li>
                ))}
                {s.items.length > 4 && (
                  <li className="opacity-60 italic pl-4">+ {s.items.length - 4} autres prestations</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Blog preview ============ */
function BlogPreview() {
  const ARTICLES = useArticles();
  return (
    <section className="paper-bg py-28 px-6 md:px-12 border-t border-[var(--ax-line)]" data-testid="blog-preview-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 reveal">
          <div>
            <div className="ax-eyebrow mb-4">— Le Journal AXIOM</div>
            <h2 className="ax-h2">Conseils & <span className="italic gold-gradient">analyses</span></h2>
          </div>
          <Link to="/blog" className="btn-outline-ink" data-testid="blog-see-all">
            Tous les articles <ChevronRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {ARTICLES.slice(0, 3).map((a) => (
            <Link to={`/blog/${a.slug}`} key={a.slug} className="article-card reveal" data-testid={`article-${a.slug}`}>
              <div className="font-mono-ax text-xs tracking-widest text-[var(--ax-gold-deep)]">{a.category.toUpperCase()}</div>
              <h3 className="font-display text-2xl leading-tight">{a.title}</h3>
              <p className="font-sans-ax text-sm text-neutral-600 leading-relaxed flex-grow">{a.excerpt}</p>
              <div className="flex items-center justify-between font-sans-ax text-xs text-neutral-500 pt-3 border-t border-[var(--ax-line)]">
                <span>{a.date}</span>
                <span>{a.readTime} de lecture</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Contact ============ */
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      const mailto = `mailto:${COMPANY.email}?subject=${encodeURIComponent("Demande via site — " + (form.subject || form.name))}&body=${encodeURIComponent(
        `Nom: ${form.name}\nEmail: ${form.email}\nTéléphone: ${form.phone}\n\n${form.message}`
      )}`;
      setStatus({ state: "ok", msg: "Message envoyé. Nous vous répondons dans les plus brefs délais.", mailto });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ state: "err", msg: "Une erreur est survenue. Merci de réessayer ou de nous contacter par téléphone." });
    }
  };

  return (
    <section id="contact" className="ink-bg py-28 px-6 md:px-12" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 reveal">
          <div className="ax-eyebrow !text-[var(--ax-gold)] mb-6">— Contact</div>
          <h2 className="ax-h2 text-[var(--ax-cream)]">
            Vous avez<br/>un <span className="italic gold-gradient">projet</span> ?
          </h2>
          <p className="font-sans-ax text-neutral-300 mt-6 leading-relaxed">
            Une question ? Contactez-nous, nous vous répondrons dans les plus brefs délais.
          </p>
          <div className="mt-10 space-y-5 font-sans-ax">
            <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="flex items-center gap-4 text-[var(--ax-cream)] hover:text-[var(--ax-gold)] transition" data-testid="contact-phone">
              <Phone size={18} className="text-[var(--ax-gold)]" /> {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 text-[var(--ax-cream)] hover:text-[var(--ax-gold)] transition break-all" data-testid="contact-email">
              <Mail size={18} className="text-[var(--ax-gold)] flex-shrink-0" /> {COMPANY.email}
            </a>
            <div className="flex items-start gap-4 text-neutral-300">
              <MapPin size={18} className="text-[var(--ax-gold)] mt-1 flex-shrink-0" /> {COMPANY.address}
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="md:col-span-7 reveal space-y-6 bg-[rgba(255,255,255,0.03)] p-8 md:p-10 border border-[rgba(201,164,74,0.2)]" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="ax-label !text-neutral-400">Nom complet *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ax-input !text-[var(--ax-cream)] !border-neutral-700 focus:!border-[var(--ax-gold)]" data-testid="contact-input-name" />
            </div>
            <div>
              <label className="ax-label !text-neutral-400">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ax-input !text-[var(--ax-cream)] !border-neutral-700 focus:!border-[var(--ax-gold)]" data-testid="contact-input-email" />
            </div>
            <div>
              <label className="ax-label !text-neutral-400">Téléphone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ax-input !text-[var(--ax-cream)] !border-neutral-700 focus:!border-[var(--ax-gold)]" data-testid="contact-input-phone" />
            </div>
            <div>
              <label className="ax-label !text-neutral-400">Sujet</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="ax-input !text-[var(--ax-cream)] !border-neutral-700 focus:!border-[var(--ax-gold)]" data-testid="contact-input-subject" />
            </div>
          </div>
          <div>
            <label className="ax-label !text-neutral-400">Votre message *</label>
            <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="ax-textarea !text-[var(--ax-cream)] !border-neutral-700 focus:!border-[var(--ax-gold)]" rows={5} data-testid="contact-input-message" />
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <button type="submit" disabled={status.state === "loading"} className="btn-gold" data-testid="contact-submit-btn">
              {status.state === "loading" ? "Envoi…" : "Envoyer le message"} <ArrowUpRight size={16} />
            </button>
            {status.state === "ok" && (
              <>
                <span className="text-[var(--ax-gold-light)] font-sans-ax text-sm" data-testid="contact-success">{status.msg}</span>
                {status.mailto && <a href={status.mailto} className="text-[var(--ax-gold)] underline text-sm font-sans-ax">Ouvrir aussi dans mon email</a>}
              </>
            )}
            {status.state === "err" && <span className="text-red-400 font-sans-ax text-sm" data-testid="contact-error">{status.msg}</span>}
          </div>
        </form>
      </div>
    </section>
  );
}

/* ============ Footer ============ */
function Footer() {
  const [open, setOpen] = useState(false);
  return (
    <footer className="bg-[var(--ax-black)] text-[var(--ax-cream)] px-6 md:px-12 pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 pb-12 border-b border-neutral-800">
          <div className="md:col-span-5">
            <div className="ax-monogram !text-[var(--ax-cream)] text-3xl">AXIOM<span className="!text-[var(--ax-gold)]">.</span></div>
            <p className="font-sans-ax text-neutral-400 mt-4 max-w-sm leading-relaxed text-sm">
              {COMPANY.tagline}. Cabinet basé dans le Nord de France, intervention nationale.
            </p>
          </div>
          <div className="md:col-span-3 font-sans-ax text-sm space-y-2">
            <div className="ax-eyebrow !text-[var(--ax-gold)] mb-3">Navigation</div>
            <a href="/#expertise" className="block text-neutral-300 hover:text-[var(--ax-gold)]">Expertise</a>
            <a href="/#about" className="block text-neutral-300 hover:text-[var(--ax-gold)]">À propos</a>
            <Link to="/blog" className="block text-neutral-300 hover:text-[var(--ax-gold)]">Journal</Link>
            <a href="/#contact" className="block text-neutral-300 hover:text-[var(--ax-gold)]">Contact</a>
          </div>
          <div className="md:col-span-4 font-sans-ax text-sm space-y-2">
            <div className="ax-eyebrow !text-[var(--ax-gold)] mb-3">Contact</div>
            <div className="text-neutral-300">{COMPANY.phone}</div>
            <div className="text-neutral-300 break-all">{COMPANY.email}</div>
            <div className="text-neutral-400 text-xs mt-2">{COMPANY.address}</div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between gap-4 pt-6 text-xs font-sans-ax text-neutral-500">
          <div>© {new Date().getFullYear()} {COMPANY.name} — {COMPANY.rcs} — Tous droits réservés.</div>
          <button onClick={() => setOpen(!open)} className="text-neutral-400 hover:text-[var(--ax-gold)] underline text-left" data-testid="footer-legal-toggle">
            {open ? "Masquer les mentions légales" : "Mentions légales"}
          </button>
        </div>
        {open && (
          <div className="mt-6 p-6 bg-neutral-900 border border-neutral-800 font-sans-ax text-xs text-neutral-400 leading-relaxed space-y-3" data-testid="legal-mentions">
            <p><strong className="text-[var(--ax-gold-light)]">1. Éditeur du site</strong><br/>
              AXIOM — Entrepreneur individuel (Micro-entreprise). RCS Dunkerque 502 895 824.<br/>
              Adresse : 991 Route du Berdyck, 59143 Millam — Tél : 07 66 10 89 64 — Email : contact.axiom.nord@gmail.com
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">2. Hébergement</strong><br/>
              Site hébergé par Emergent / WordPress.com — Automattic Inc., 60 29th Street #343, San Francisco, CA 94110, USA.
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">3. Activité</strong><br/>
              AXIOM exerce une activité commerciale de conseil en gestion et développement d'entreprise, accompagnement à la création, formation professionnelle et services aux particuliers.
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">4. TVA</strong><br/>
              TVA non applicable, art. 293 B du CGI.
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">5. Propriété intellectuelle</strong><br/>
              L'ensemble du contenu du site (textes, images, logo) est la propriété exclusive d'AXIOM. Toute reproduction, même partielle, est interdite sans autorisation préalable.
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">6. Données personnelles & RGPD</strong><br/>
              Conformément au RGPD et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Contact : contact.axiom.nord@gmail.com.
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">7. Cookies</strong><br/>
              Le site peut utiliser des cookies à des fins de mesure d'audience. Vous pouvez les désactiver dans votre navigateur.
            </p>
            <p><strong className="text-[var(--ax-gold-light)]">8. Droit applicable</strong><br/>
              Droit français. Tout litige sera soumis aux tribunaux compétents français.
            </p>
          </div>
        )}
      </div>
    </footer>
  );
}

/* ============ Home page ============ */
function Home() {
  useReveal();
  return (
    <>
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <About />
      <BlogPreview />
      <Contact />
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

/* ============ Blog list page ============ */
function BlogList() {
  useReveal();
  const ARTICLES = useArticles();
  return (
    <>
      <Nav />
      <section className="paper-bg pt-36 pb-20 px-6 md:px-12" data-testid="blog-list-page">
        <div className="max-w-7xl mx-auto">
          <div className="ax-eyebrow mb-6">— Le Journal AXIOM</div>
          <h1 className="ax-h1">Conseils &<br/><span className="italic gold-gradient">analyses</span></h1>
          <p className="font-sans-ax text-neutral-600 max-w-2xl mt-8 text-lg leading-relaxed">
            Tribunes, retours d'expérience et décryptages signés par les conseillers AXIOM, pour les dirigeants, créateurs d'entreprise et particuliers.
          </p>
        </div>
      </section>
      <section className="paper-bg pb-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {ARTICLES.map((a) => (
            <Link to={`/blog/${a.slug}`} key={a.slug} className="article-card reveal" data-testid={`blog-item-${a.slug}`}>
              <div className="font-mono-ax text-xs tracking-widest text-[var(--ax-gold-deep)]">{a.category.toUpperCase()}</div>
              <h3 className="font-display text-2xl leading-tight">{a.title}</h3>
              <p className="font-sans-ax text-sm text-neutral-600 leading-relaxed flex-grow">{a.excerpt}</p>
              <div className="flex items-center justify-between font-sans-ax text-xs text-neutral-500 pt-3 border-t border-[var(--ax-line)]">
                <span>{a.date}</span>
                <span>{a.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

/* ============ Blog detail page ============ */
function BlogDetail() {
  useReveal();
  const ARTICLES = useArticles();
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) {
    return (
      <>
        <Nav />
        <section className="paper-bg pt-36 pb-28 px-6 md:px-12 min-h-screen" data-testid="blog-not-found">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="ax-h2">Article introuvable</h1>
            <Link to="/blog" className="btn-gold mt-8 inline-flex">Retour au journal</Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }
  return (
    <>
      <Nav />
      <article className="paper-bg pt-32 pb-20 px-6 md:px-12" data-testid="blog-article">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="font-sans-ax text-xs tracking-widest text-neutral-500 hover:text-[var(--ax-gold-deep)]">← TOUS LES ARTICLES</Link>
          <div className="ax-eyebrow mt-8">— {article.category}</div>
          <h1 className="ax-h2 mt-4">{article.title}</h1>
          <div className="flex gap-6 font-sans-ax text-sm text-neutral-500 mt-6">
            <span>{article.date}</span><span>•</span><span>{article.readTime} de lecture</span>
          </div>
          <div className="hero-line my-12" />
          <div className="font-display text-xl leading-relaxed text-neutral-800 space-y-5 whitespace-pre-line">
            {article.content.split('\n').map((p, i) => {
              if (p.startsWith('**') && p.endsWith('**')) {
                return <h3 key={i} className="font-display text-2xl text-[var(--ax-gold-deep)] mt-10 mb-2">{p.replace(/\*\*/g, '')}</h3>;
              }
              return p.trim() ? <p key={i}>{p}</p> : null;
            })}
          </div>
          <div className="mt-16 pt-10 border-t border-[var(--ax-line)] flex flex-wrap gap-4">
            <Link to="/blog" className="btn-outline-ink">← Autres articles</Link>
            <a href="/#contact" className="btn-gold">Discuter avec nos conseillers <ArrowUpRight size={16} /></a>
          </div>
        </div>
      </article>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
